import { Document, Font } from "@react-pdf/renderer";
import { useMemo } from "react";

// ?url suffix imports give Vite the hashed asset URL at build time without emitting
// automatic <link rel="preload"> hints — fonts are only fetched by @react-pdf/renderer
// internally and are never used as CSS fonts, so browser preloads would go unused.
import LatoBold from "assets/fonts/Lato/Lato-Bold.ttf?url";
import LatoBoldItalic from "assets/fonts/Lato/Lato-BoldItalic.ttf?url";
import LatoExtraBold from "assets/fonts/Lato/Lato-ExtraBold.ttf?url";
import LatoRegularItalic from "assets/fonts/Lato/Lato-Italic.ttf?url";
import LatoRegular from "assets/fonts/Lato/Lato-Regular.ttf?url";
import LatoSemiBold from "assets/fonts/Lato/Lato-SemiBold.ttf?url";
import LatoThin from "assets/fonts/Lato/Lato-Thin.ttf?url";
import TinosBold from "assets/fonts/Tinos/Tinos-Bold.ttf?url";
import TinosBoldItalic from "assets/fonts/Tinos/Tinos-BoldItalic.ttf?url";
import TinosItalic from "assets/fonts/Tinos/Tinos-Italic.ttf?url";
import TinosRegular from "assets/fonts/Tinos/Tinos-Regular.ttf?url";

import PdfDocumentPageItem from "./pdf-document-page-item.component";
import { PdfDocumentPage } from "./pdf-document-page.type";
import { FORM_FONT_FAMILIES, PDF_FONT_FACES } from "./pdf-fonts";

Font.register({
  family: "LatoRegular",
  format: "truetype",
  src: LatoRegular,
  fontWeight: "normal"
});

Font.register({
  family: "LatoRegular",
  format: "truetype",
  fonts: [
    {
      src: LatoThin,
      fontWeight: "thin",
      fontSize: "14"
    },
    {
      src: LatoRegular,
      fontWeight: 400,
      fontSize: "14"
    },
    {
      src: LatoRegularItalic,
      fontWeight: 400, // Lato-Italic.ttf is regular-weight italic, not bold
      fontStyle: "italic",
      fontSize: "14"
    },
    {
      src: LatoSemiBold,
      fontWeight: 600,
      fontSize: "14"
    },
    {
      src: LatoBold,
      fontWeight: 700,
      fontSize: "14"
    },
    {
      src: LatoBoldItalic,
      fontWeight: 700,
      fontStyle: "italic",
      fontSize: "14"
    },
    {
      src: LatoExtraBold,
      fontWeight: 800,
      fontSize: "14"
    }
  ]
});

Font.register({
  family: "Tinos",
  format: "truetype",
  fonts: [
    {
      src: TinosRegular,
      fontWeight: "normal",
      fontSize: "16"
    },
    {
      src: TinosItalic,
      fontWeight: "normal",
      fontStyle: "italic",
      fontSize: "16"
    },
    {
      src: TinosBoldItalic,
      fontWeight: "bold",
      fontStyle: "italic",
      fontSize: "16"
    },
    {
      src: TinosBold,
      fontWeight: "bold",
      fontSize: "16"
    }
  ]
});

// ENG-2277: the Arial / Helvetica stand-in for HTML Forms (metric-compatible).
Font.register({
  family: FORM_FONT_FAMILIES.sans,
  fonts: PDF_FONT_FACES.filter(
    (face) => face.family === FORM_FONT_FAMILIES.sans
  ).map(({ src, weight, style }) => ({
    src,
    fontWeight: weight,
    fontStyle: style
  }))
});

// Never break words in prose — react-pdf's default would hyphenate English
// text, and a mid-word break in a chart note or letter is worse than a long
// word overflowing its (page-wide) line. Narrow TABLE cells are the one
// place tokens genuinely can't fit and must wrap; those get a scoped,
// money-aware callback per <Text> node in PdfDocumentTable instead of a
// global override, so nothing outside Enhanced Table Rendering changes.
Font.registerHyphenationCallback((word) => [word]);

// Eagerly fetch fonts on module load so PDF generation doesn't block on 15s downloads
Font.load({ fontFamily: "LatoRegular", fontStyle: "normal", fontWeight: 400 });
Font.load({ fontFamily: "LatoRegular", fontStyle: "normal", fontWeight: 700 });

type PdfDocumentProps = {
  pages: PdfDocumentPage[];
  title: string;
  fitFooter?: boolean;
};

const PAGE_BREAK_PLACEHOLDER = "<!-- pagebreak -->";

export default function PdfDocument({
  pages,
  title,
  fitFooter
}: PdfDocumentProps) {
  //Do not use feature flags here, in this component they will not work

  const documentPages = useMemo(() => {
    const result: PdfDocumentPage[] = [];

    pages.forEach((page) => {
      const { bodyHtml } = page;

      if (!bodyHtml?.includes(PAGE_BREAK_PLACEHOLDER)) {
        result.push(page);
      } else {
        bodyHtml.split(PAGE_BREAK_PLACEHOLDER).forEach((bodyHtml, index) => {
          const isFirstPage = index === 0;
          result.push({
            ...page,
            header: isFirstPage ? page.header : undefined,
            children: isFirstPage ? page.children : undefined,
            bodyHtml
          });
        });
      }
    });

    return result;
  }, [pages]);

  return (
    <Document title={title}>
      {documentPages.map(
        (
          {
            dpi = 128,
            header,
            footer,
            contentStyle,
            bodyStyle = {},
            bodyHtml,
            children,
            showPageNumber = true,
            pageNumberPrefix = "",
            pageNumberPostfix = "",
            pdfNativeTablesEnabled = false,
            printableTableLayoutFixesEnabled = false,
            formSettings
          },
          index
        ) => {
          if (bodyHtml?.includes(PAGE_BREAK_PLACEHOLDER)) {
            return null;
          }

          return (
            <PdfDocumentPageItem
              key={index}
              dpi={dpi}
              header={header}
              footer={footer}
              contentStyle={contentStyle}
              bodyStyle={bodyStyle}
              bodyHtml={bodyHtml}
              pdfNativeTablesEnabled={pdfNativeTablesEnabled}
              printableTableLayoutFixesEnabled={printableTableLayoutFixesEnabled}
              children={children}
              showPageNumber={showPageNumber}
              pageNumberPrefix={pageNumberPrefix}
              pageNumberPostfix={pageNumberPostfix}
              formSettings={formSettings}
              fitFooter={fitFooter}
            />
          );
        }
      )}
    </Document>
  );
}
